import { useLocalization } from '@components/Localization'
import LocalizedLink from '@components/Localization/LocalizedLink'
import { Box, breakpoints, theme } from '@gassan-ui'
import { Icon, Span } from '@rolex-ui'
import React, { FC } from 'react'
import styled from 'styled-components'

const IconCircle = styled.div`
  width: 50px;
  height: 50px;
  background: transparent;
  border-radius: 25px;
  display: flex;
  align-items: center;
  justify-content: center;
  color: ${p => p.theme.rolex.colors.primary};
  transition: all 0.3s ease-in-out;
  @media screen and (min-width: ${breakpoints.large}) {
    border-color: white;
    color: white;
    border-radius: 17.5px;
    margin-right: 15px;
    width: 35px;
    height: 35px;
  }
`

const RowLink = styled.a<{ variant: 'white' | 'transparent' }>`
  transition: color 0.2s ease-in-out;
  display: flex;
  flex-wrap: wrap;
  margin: 0 10px;
  align-items: center;
  font-size: 14px;

  @media screen and (min-width: ${breakpoints.large}) {
    margin: 8px 0;
  }

  ${IconCircle} {
    color: ${p => p.variant === 'transparent' && p.theme.rolex.colors.primary};
    border: ${p => p.variant === 'transparent' && `1px solid ${theme.rolex.colors.primary}`};
    background: ${p => (p.variant === 'transparent' ? 'transparent' : 'white')};

    @media screen and (min-width: ${breakpoints.large}) {
      color: ${p => (p.variant === 'transparent' ? 'white' : p.theme.rolex.colors.primary)};
      border: ${p => p.variant === 'transparent' && '1px solid white'};
    }
  }

  span {
    transition: all 0.3s ease-in-out;
  }

  @media (hover: hover) and (pointer: fine) {
    &:hover {
      span {
        opacity: 0.3;
      }

      ${IconCircle} {
        background: ${p => (p.variant === 'transparent' ? 'white' : p.theme.rolex.colors.primary)};
        color: ${p => (p.variant === 'transparent' ? p.theme.rolex.colors.primary : 'white')};
      }
    }
  }
`

interface Props {
  variant?: 'white' | 'transparent'
  isHorizontal?: boolean
  sendMessage?: boolean
  findStore?: boolean
  phoneNumber?: string
  directionsUrl?: string
  detailSlug?: string
}

const CtaButtons: FC<Props> = ({
  variant = 'transparent',
  isHorizontal = false,
  sendMessage = false,
  findStore = false,
  phoneNumber,
  directionsUrl,
  detailSlug
}) => {
  const { messages, lang } = useLocalization()

  const handleSmoothScroll = e => {
    e.preventDefault()
    const contact = document.getElementById('contact')

    if (contact) {
      window.scroll({ top: contact.offsetTop, left: 0, behavior: 'smooth' })
    }
  }

  const handleDirectionsClick = (e, href: string) => {
    e.preventDefault()
    if (['staging', 'production'].includes(process.env.NEXT_PUBLIC_STAGE)) {
      // @ts-ignore
      _satellite.track('getDirections')
    }
    window.open(href, '_blank')
  }

  const handlePhoneClick = (e, href: string) => {
    e.preventDefault()
    if (window.innerWidth < 769) {
      ;['staging', 'production'].includes(process.env.NEXT_PUBLIC_STAGE) &&
        // @ts-ignore
        _satellite.track('contactCall')
    }
    window.location.href = href
  }

  const handleFindStoreClick = e => {
    e.preventDefault()
    if (['staging', 'production'].includes(process.env.NEXT_PUBLIC_STAGE)) {
      // @ts-ignore
      _satellite.track('findStore')
    }
    window.location.href = `/${lang}/rolex/contact`
  }

  return (
    <Box
      display="flex"
      as="ul"
      justifyContent={{ _: 'center', large: isHorizontal ? 'space-evenly' : 'center' }}
      flexDirection={{ _: 'row', large: isHorizontal ? 'row' : 'column' }}
    >
      {phoneNumber && (
        <RowLink
          onClick={e => handlePhoneClick(e, `tel:${phoneNumber}`)}
          title={`${messages.rolex.cta.call} ${phoneNumber}`}
          variant={variant}
        >
          <IconCircle>
            <Icon size={20} icon="phone"></Icon>
          </IconCircle>
          <Span display={{ _: 'none', large: 'block' }} fontWeight={300}>
            {messages.rolex.cta.call} {phoneNumber}
          </Span>
        </RowLink>
      )}
      {sendMessage && (
        <RowLink
          href="#contact"
          onClick={handleSmoothScroll}
          title={messages.rolex.cta.message}
          variant={variant}
        >
          <IconCircle>
            <Icon size={20} icon="mail"></Icon>
          </IconCircle>
          <Span display={{ _: 'none', large: 'block' }} fontWeight={300}>
            {messages.rolex.cta.message}
          </Span>
        </RowLink>
      )}
      {findStore && (
        <RowLink
          onClick={e => handleFindStoreClick(e)}
          title={messages.rolex.cta.store}
          variant={variant}
        >
          <IconCircle>
            <Icon size={20} icon="location"></Icon>
          </IconCircle>
          <Span display={{ _: 'none', large: 'block' }} fontWeight={300}>
            {messages.rolex.cta.store}
          </Span>
        </RowLink>
      )}
      {directionsUrl && (
        <RowLink
          onClick={e => handleDirectionsClick(e, directionsUrl)}
          title={messages.rolex.cta.getDirections}
          target="blank"
          variant={variant}
        >
          <IconCircle>
            <Icon size={20} icon="directions"></Icon>
          </IconCircle>
          <Box
            display={{ _: 'none', large: 'flex' }}
            fontWeight={300}
            alignItems="center"
            style={{ fontFamily: 'Helvetica, sans-serif' }}
          >
            {messages.rolex.cta.getDirections}
            <Icon ml="1" size={[16, 20]} icon="externalLink"></Icon>
          </Box>
        </RowLink>
      )}
      {detailSlug && (
        <LocalizedLink as={`/rolex/contact/${detailSlug}`} href="/rolex/contact/[sid]">
          <RowLink
            href={`/rolex/contact/${detailSlug}`}
            title={messages.rolex.cta.moreDetails}
            variant={variant}
          >
            <IconCircle>
              <Icon size={20} icon="plus"></Icon>
            </IconCircle>
            <Span display={['none', 'block']} fontWeight={300}>
              {messages.rolex.cta.moreDetails}
            </Span>
          </RowLink>
        </LocalizedLink>
      )}
    </Box>
  )
}

export default CtaButtons
